import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { FeatureAccessService } from './feature-access.service';
import { PermissionGateService } from './permission-gate.service';

/**
 * UC-16 (#9297) route guard, two layers:
 *
 *  1. Tenant enablement — the `tblPage` row `PageName='ControlTower'` must be
 *     active (cached sidebar payload, synchronous).
 *  2. Caller permission (#9888) — the user must hold `control-tower.view`
 *     via the RBAC store (SuperAdmin short-circuits inside `has$`).
 *
 * `has$` default-denies until the MePermissions slice hydrates, so the guard
 * waits for the slice to settle (data or error) before deciding — otherwise
 * every cold navigation would bounce legitimate users. A load failure stays
 * fail-closed: no permissions proven, no page.
 *
 * Denies land on `/dashboard` — the app's actual landing route (`'' →
 * 'dashboard'` in app.routes.ts), not `/home`.
 */
export const controlTowerGuard: CanActivateFn = () => {
  const featureAccess = inject(FeatureAccessService);
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  if (!featureAccess.isPageActive('control-tower')) {
    router.navigateByUrl('dashboard');
    return false;
  }

  return combineLatest([
    permissionGate.has$('control-tower.view'),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, me, error]) => me != null || error != null),
    take(1),
    map(([allowed]) => (allowed ? true : router.parseUrl('dashboard'))),
  );
};
